import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Input } from "antd";
import { AxiosError } from "axios";
import ApproveRequestDropdown from "../../../components/Button/ApproveRequestDropdown";
import FormBuilderCancelButton from "../../../components/Button/FormBuilderCancelButton";
import FormBuilderSaveAsDraftButton from "../../../components/Button/FormBuilderSaveAsDraftButton";
import FormBuilderPopconfirmSubmitButton from "../../../components/Button/FormBuilderPopconfirmSubmitButton";
import UpdateClosedReportButton from "../../../components/Button/UpdateClosedReportButton";
import FormValidationStatus from "../../../components/Form/FormValidationStatus";
import CustomPopconfirm from "../../../components/Popover/CustomPopconfirm";
import { ReportingFormFooterProps } from "../../../types/reporting.types";
import { alertMessage } from "../../../utils/alert";
import { getReportingPermissions } from "../../../utils/reportingPermissions";
import {
	notifySyncFailures,
	notifySyncResult,
} from "../../../utils/reporting/reportSaveFeedback";
import { validateTenantId } from "../../../utils/tenantUtils";

function AuditReportFormFooter({
	form,
	reportContext,
	isSaving,
	isLoading,
	handleClose,
	handleDelete,
	saveReport,
	onValidationError,
	onValidationSuccess,
	onExitEditMode,
}: ReportingFormFooterProps) {
	const {
		status,
		openStatus,
		isAuthor,
		isReviewer,
		isResponsible,
		mode,
		isInternalReport,
	} = reportContext;

	const isSavingOrLoading = isSaving || isLoading;
	const permissions = getReportingPermissions(reportContext);

	const stripNewFindingFlags = (findings: any[] | undefined) =>
		findings?.map((finding) => {
			const rest = { ...finding };
			delete rest.is_new;
			return rest;
		});

	const handleRequestChanges = async () => {
		try {
			const findings = stripNewFindingFlags(
				form.formData?.findings?.map((finding: any) => ({
					...finding,
					status: finding.already_completed ? "completed" : "draft",
				}))
			);

			const nextOpenStatus =
				openStatus === "in_review" ? "changes_required" : "revision_required";

			const data = {
				request_changes_comment: form.formData?.request_changes_comment,
				open_status: nextOpenStatus,
				findings: findings,
			};

			const { sync } = await saveReport(data, { mode: "edit" });

			// The status change has happened even if attachments did not sync.
			alertMessage(
				"success",
				openStatus === "in_review"
					? "Changes requested and sent back to the author."
					: "Changes requested and sent back to responsible person(s) for revision."
			);
			notifySyncFailures(sync);
		} catch (error) {
			console.error(error);
			alertMessage("error", "Failed to request changes. Please try again.");
		}
	};

	const handleApprove = async () => {
		try {
			const isInitialReview = openStatus === "in_review";
			const data = {
				...form.formData,
				findings: stripNewFindingFlags(form.formData?.findings),
				status: isInitialReview ? "open" : "closed",
				// The final approval overwrites approved_on, so the initial one is
				// kept as reviewed_on for the findings' stages.
				...(isInitialReview
					? {
							open_status: "in_progress",
							approved_on: new Date(),
							reviewed_on: new Date(),
						}
					: { approved_on: new Date(), closed_on: new Date() }),
			};

			const { sync } = await saveReport(data, { mode: "edit" });
			notifySyncFailures(sync);
		} catch (error) {
			console.error(error);
			alertMessage("error", "Failed to approve. Please try again.");
		}
	};

	const handleSaveAsDraft = async () => {
		try {
			const operator = form.formData?.operator;
			if (!validateTenantId(operator)) throw new Error("Invalid operator");

			const findings = stripNewFindingFlags(
				form.formData?.findings?.map((finding: any) => ({
					...finding,
					status: "pending",
				}))
			);

			const data = {
				...form.formData,
				status: "draft",

				findings: findings,
			};

			const { sync } = await saveReport(data, { mode });
			notifySyncResult(sync, "Audit report has been saved as draft.");
			await onExitEditMode?.();
		} catch (error) {
			console.error(error);
			if (error instanceof AxiosError && error?.status === 409) {
				alertMessage(
					"error",
					`This audit report already exists, please ensure the audit report is unique. Changes are not saved.`
				);
			} else {
				alertMessage("error", "Failed to save. Please try again.");
			}
		}
	};

	const handleSubmitForReview = async () => {
		try {
			const operator = form.formData?.operator;
			if (!validateTenantId(operator)) throw new Error("Invalid operator");

			const findings = stripNewFindingFlags(
				form.formData?.findings?.map((finding: any) => ({
					...finding,
					status: finding.already_completed
						? "completed"
						: finding.status || "pending",
				}))
			);

			const data = {
				...form.formData,
				status: "open",
				open_status: "in_review",
				submitted_on: new Date(),

				findings: findings,
			};

			const { sync } = await saveReport(data, { mode });
			notifySyncResult(sync, "Audit report has been submitted for review.");
		} catch (error) {
			console.error(error);
			if (error instanceof AxiosError && error?.status === 409) {
				alertMessage(
					"error",
					`This Audit report already exists, please ensure the Audit report is unique. Changes are not saved.`
				);
			} else {
				alertMessage("error", "Failed to submit for review. Please try again.");
			}
		}
	};

	const handleUpdate = async () => {
		try {
			const { sync } = await saveReport(
				{
					...form.formData,
					findings: stripNewFindingFlags(form.formData?.findings),
					preserve_status: true,
				},
				{ mode: "edit" }
			);
			notifySyncResult(sync, "Audit report has been updated.");
		} catch (error) {
			console.error(error);
			alertMessage("error", "Failed to update. Please try again.");
		}
	};

	return (
		<>
			{/**
			 * Delete button
			 * Conditions:
			 * 1. Edit mode (saved in db)
			 * 2. By author when report is still in draft mode
			 * 3. By reviewer when report is being reviewed (in_review or final_review) and is not closed
			 */}
			{mode === "edit" && permissions.canDelete ? (
				<CustomPopconfirm
					title="Permanently delete this item? This action cannot be undone."
					handleConfirm={handleDelete}
				>
					<Button
						className="ant-btn-secondary"
						danger
						disabled={isSavingOrLoading}
					>
						Delete
					</Button>
				</CustomPopconfirm>
			) : (
				<div />
			)}
			{/* Flex spacer to prevent overlap between Delete button and validation message/buttons */}
			<div style={{ flex: 1, minWidth: 24 }} />
			<div className="flex gap-2 items-center">
				<FormValidationStatus form={form} reportType="audit" mode={mode} />
				{/**
				 * Close / Cancel button
				 * Label switches based on unsaved changes:
				 * - Clean form: "Close" closes the drawer immediately.
				 * - Dirty form: "Cancel" prompts to discard unsaved changes.
				 */}
				<FormBuilderCancelButton
					form={form}
					handleFormCancel={handleClose}
					alwaysEnabled={true}
					isLoading={isSavingOrLoading}
				/>

				{/**
				 * Save as draft button
				 * Conditions:
				 * 1. Only shown to Author when report is in create mode or draft mode
				 */}
				{permissions.canSubmit &&
					(mode === "create" ||
						status === "draft" ||
						openStatus === "changes_required") && (
						<FormBuilderSaveAsDraftButton
							form={form}
							fieldsToWatch={["title"]}
							handleFormSave={handleSaveAsDraft}
							isLoading={isSavingOrLoading}
						/>
					)}

				{/**
				 * Submit button
				 * Conditions:
				 * 1. Only shown to Author when report is in create mode or draft mode
				 */}
				{permissions.canSubmit &&
					(mode === "create" ||
						status === "draft" ||
						openStatus === "changes_required") && (
						<FormBuilderPopconfirmSubmitButton
							form={form}
							labelText="Submit"
							title={
								<div className="max-w-2xs p-2.5">
									Submit audit report? Once submitted, the report cannot be
									edited.
									<br />
									The reviewer will be notified to review the report.
								</div>
							}
							onConfirm={handleSubmitForReview}
							isLoading={isSavingOrLoading}
							onValidationError={onValidationError}
						/>
					)}
				{/**
				 * Update button
				 * Conditions:
				 * 1. Only shown when report is not draft and admin is editing
				 */}
				{mode === "edit" && status !== "draft" && permissions.canAdminSave && (
					<UpdateClosedReportButton
						form={form}
						isSavingOrLoading={isSavingOrLoading}
						handleUpdate={handleUpdate}
						onValidationError={onValidationError}
						onValidationSuccess={onValidationSuccess}
						status={status}
					/>
				)}

				{/**
				 * Approve + Request changes dropdown
				 * Conditions:
				 * 1. Only shown to reviewer when report is open and being reviewed (final_review)
				 */}
				<ApproveRequestDropdown
					form={form}
					isSavingOrLoading={isSavingOrLoading}
					handleApprove={handleApprove}
					handleRequestChanges={handleRequestChanges}
					onValidationError={onValidationError}
					approveTitle={
						<div className="max-w-2xs p-2.5 text-justify">
							{openStatus === "in_review"
								? "Approve audit report? The report will move to in progress and findings will be assigned to responsible persons."
								: isInternalReport
									? "Approve audit report? It will close and released to the organization after submission."
									: "Approve audit report? It will close and shared to all tenants."}
						</div>
					}
					requestChangesContent={
						<div className="flex flex-col gap-2">
							<div className="flex flex-nowrap gap-2">
								<ExclamationCircleOutlined />
								<span className="text-justify flex items-center gap-2">
									{openStatus === "in_review"
										? "Assigned roles won’t be notified. This report will return to the author for revision. Please add a comment to explain why."
										: "Assigned roles won’t be notified. This report will be returned to responsible persons for revision. Please add a comment to explain why."}
								</span>
							</div>
							<Input.TextArea
								autoSize={{ minRows: 3 }}
								placeholder="Comments"
								value={form.formData?.request_changes_comment}
								onChange={(e) =>
									form.setFormData({
										...form.formData,
										request_changes_comment: e.target.value,
									})
								}
							/>
						</div>
					}
					canApprove={
						permissions.canApprove &&
						status === "open" &&
						["in_review", "final_review"].includes(openStatus ?? "")
					}
					canRequestChanges={
						permissions.canRequestChanges &&
						status === "open" &&
						["in_review", "final_review"].includes(openStatus ?? "")
					}
				/>
			</div>
		</>
	);
}

export default AuditReportFormFooter;
